import { isDevelopment } from '@/lib/config';
import { FEATURE_FLAGS } from '@/lib/feature-flags';
import {
  getFeatureFlag,
  getFeatureFlagSync,
  getMixpanelDistinctId,
  registerSuperProperties,
} from '@/lib/mixpanel';

/**
 * Real-time transport resolution.
 *
 * The Mixpanel feature gate `stream` (see {@link DEAL_SSE_FEATURE_FLAG}) decides,
 * in every environment: `On` → the SSE deal-events channel, otherwise the GraphQL
 * WebSocket subscriptions. Each environment reads the gate of its own Mixpanel
 * project (Smart Offers – Dev outside production), so the rollout is driven from
 * Mixpanel without a deploy. Once the flags are loaded, a project without the
 * gate reads as `Off` (WebSocket). Only when no gate value is known — Mixpanel
 * not configured (local dev without token), flags not loaded and nothing cached
 * for this user — does the environment default apply: SSE outside production,
 * WebSocket in production. Nothing overrides the gate: to move a user, target
 * them in Mixpanel.
 *
 * This lives in its own module (not `config.ts`) so the super-property helper's
 * Mixpanel import doesn't create a config -> mixpanel import cycle.
 */

export type DealRealtimeTransport = 'sse' | 'ws';

/** Mixpanel feature gate that decides the real-time transport. */
export const DEAL_SSE_FEATURE_FLAG = FEATURE_FLAGS.dealRealtimeSse;

/**
 * Last value of {@link DEAL_SSE_FEATURE_FLAG} seen by this browser, used when
 * the flags are not loaded yet at the moment the transport is resolved.
 *
 * Stored with the Mixpanel `distinct_id` it was assigned to and only read back
 * for that same id: an assignment never leaks to another account sharing the
 * browser, nor from the anonymous id to the user once identified.
 */
const FLAG_CACHE_KEY = 'dealRealtimeSseFlag';

type CachedFlag = { distinctId: string; enabled: boolean };

/**
 * Transport from the feature gate, pinned for the lifetime of the page: the
 * gate is read once, and a value arriving or changing later only applies on the
 * next load — never by switching transports under an open deal.
 */
let pinnedFlagTransport: DealRealtimeTransport | null = null;

function readCachedFlag(distinctId: string | null): boolean | null {
  if (!distinctId) return null;
  try {
    const raw = window.localStorage.getItem(FLAG_CACHE_KEY);
    const cached = raw ? (JSON.parse(raw) as Partial<CachedFlag>) : null;
    return cached?.distinctId === distinctId &&
      typeof cached.enabled === 'boolean'
      ? cached.enabled
      : null;
  } catch {
    return null;
  }
}

function writeCachedFlag(distinctId: string | null, enabled: boolean): void {
  if (!distinctId) return;
  try {
    const cached: CachedFlag = { distinctId, enabled };
    window.localStorage.setItem(FLAG_CACHE_KEY, JSON.stringify(cached));
  } catch {
    /* localStorage unavailable — the next load falls back to the environment
       default until the flags answer in time. */
  }
}

/**
 * Resolve the real-time transport for the current page from the `stream` feature
 * gate: the gate if Mixpanel already has it, else the value cached for this
 * user, else the environment default. Pinned on first read.
 */
export function dealRealtimeTransport(): DealRealtimeTransport {
  if (typeof window === 'undefined') return 'ws';
  if (pinnedFlagTransport) return pinnedFlagTransport;

  const distinctId = getMixpanelDistinctId();
  const live = getFeatureFlagSync(DEAL_SSE_FEATURE_FLAG);
  if (live !== null) writeCachedFlag(distinctId, live);
  const enabled = live ?? readCachedFlag(distinctId) ?? isDevelopment;
  pinnedFlagTransport = enabled ? 'sse' : 'ws';
  return pinnedFlagTransport;
}

/**
 * Wait for the Mixpanel flags and store the `stream` gate for the next load.
 *
 * Does not touch the transport of the current page (see
 * {@link pinnedFlagTransport}): it only makes sure a browser whose flags were
 * late this time starts on the right transport next time — and that turning the
 * gate off reaches everyone on their next load.
 */
export async function refreshDealRealtimeFlagCache(): Promise<void> {
  if (typeof window === 'undefined') return;
  const distinctId = getMixpanelDistinctId();
  const enabled = await getFeatureFlag(DEAL_SSE_FEATURE_FLAG);
  // `identify` changed the id while the flags were loading: this answer belongs
  // to the previous id, the refetch `identify` triggers will be cached next time.
  if (enabled === null || getMixpanelDistinctId() !== distinctId) return;
  writeCachedFlag(distinctId, enabled);
}

export function __resetDealRealtimeFlagForTests(): void {
  pinnedFlagTransport = null;
}

/** Whether the SSE deal-events channel replaces the WebSocket subscriptions. */
export function isDealEventsSseEnabled(): boolean {
  return dealRealtimeTransport() === 'sse';
}

/**
 * Register the resolved real-time transport as a Mixpanel super property so
 * every event (SSE/WS lifecycle and all others) can be segmented by which
 * transport the user is on. No-op until Mixpanel is initialized.
 */
export function registerDealRealtimeTransportProperty(): void {
  registerSuperProperties({ dealRealtimeTransport: dealRealtimeTransport() });
}
